<template>
  <div id="dialog-curriculumView">
    <div class="curriculum-view">

      <div class="header">
        <p @click="handleClose"><i class="ph-bold ph-arrow-left"></i></p>
        <h2>{{ lesson?.title || '레슨' }}</h2>
        <p style="opacity:0;"><i class="ph ph-bookmark"></i></p>
      </div>

      <div class="content" ref="contentRef">
        <!-- 영상 콘텐츠 -->
        <div v-if="lesson?.content_type === 'video'" class="video-container">
          <div class="video-wrapper">
            <div :id="'youtube-player-' + lesson.idx" class="youtube-player"></div>
            <div v-if="!isVideoReady" class="video-loading">
              <i class="ph ph-spinner"></i>
              <p>영상 로딩중...</p>
            </div>
          </div>
          
          <!-- 자동 진행률 표시 -->
          <div class="auto-progress">
            <div class="progress-header">
              <span class="progress-label">시청 진행률</span>
              <span class="progress-value">{{ currentProgress }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: currentProgress + '%' }"></div>
            </div>
            <div class="progress-detail">
              <span>{{ formatTime(watchedTime) }} / {{ formatTime(totalDuration) }} 시청</span>
              <span v-if="currentProgress < 90" class="requirement">90% 이상 시청 시 완료 가능</span>
              <span v-else class="complete-ready">✓ 완료 가능</span>
            </div>
          </div>
        </div>

        <!-- 글 콘텐츠 -->
        <div v-else-if="lesson?.content_type === 'article'" class="article-container">
          <div class="article-content" v-html="renderedContent"></div>
          
          <!-- 읽기 진행률 -->
          <div class="auto-progress">
            <div class="progress-header">
              <span class="progress-label">읽기 진행률</span>
              <span class="progress-value">{{ readingProgress }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: readingProgress + '%' }"></div>
            </div>
            <div class="progress-detail">
              <span>체류 시간: {{ formatTime(stayTime) }}</span>
              <span v-if="!canCompleteArticle" class="requirement">
                끝까지 스크롤 + {{ Math.max(0, minStayTime - stayTime) }}초 더 읽기
              </span>
              <span v-else class="complete-ready">✓ 완료 가능</span>
            </div>
          </div>
        </div>

        <!-- 레슨 정보 & 완료 -->
        <div class="lesson-info-section">
          <div class="info-row" v-if="lesson?.duration">
            <i class="ph ph-clock"></i>
            <span>예상 소요시간: {{ formatDuration(lesson.duration) }}</span>
          </div>
          
          <div class="complete-section">
            <button 
              v-if="currentStatus === 'completed'"
              class="complete-btn completed"
              disabled
            >
              <i class="ph-fill ph-check-circle"></i>
              완료됨
            </button>
            <button 
              v-else
              class="complete-btn"
              :class="{ 'can-complete': canComplete }"
              :disabled="!canComplete"
              @click="markComplete"
            >
              <i class="ph ph-check-circle"></i>
              {{ canComplete ? '완료로 표시' : '조건 미충족' }}
            </button>
          </div>
        </div>

        <!-- 이전/다음 레슨 -->
        <div class="navigation-section">
          <button 
            class="nav-btn prev"
            :disabled="!prevLesson"
            @click="goToLesson(prevLesson)"
          >
            <i class="ph ph-caret-left"></i>
            <div class="nav-info" v-if="prevLesson">
              <span class="nav-label">이전 레슨</span>
              <span class="nav-title">{{ prevLesson?.title }}</span>
            </div>
            <span v-else class="nav-label">이전 레슨 없음</span>
          </button>
          
          <button 
            class="nav-btn next"
            :disabled="!nextLesson"
            @click="goToLesson(nextLesson)"
          >
            <div class="nav-info" v-if="nextLesson">
              <span class="nav-label">다음 레슨</span>
              <span class="nav-title">{{ nextLesson?.title }}</span>
            </div>
            <span v-else class="nav-label">다음 레슨 없음</span>
            <i class="ph ph-caret-right"></i>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { inject, ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue"
import { useBackButton } from '@/utils/useBackBtn'

export default {
  props: {
    lesson: Object,
    allLessons: Array,
    windowOnOff: Function,
    onProgressUpdate: Function
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const getMbid = inject('getMbid')

    useBackButton(() => {
      handleClose()
    })

    const contentRef = ref(null)
    const currentProgress = ref(0)
    const currentStatus = ref('not_started')
    
    // 영상 관련
    const isVideoReady = ref(false)
    const player = ref(null)
    const watchedTime = ref(0)
    const totalDuration = ref(0)
    const watchedSegments = ref([]) // 시청한 구간 기록
    let progressInterval = null

    // 글 관련
    const readingProgress = ref(0)
    const stayTime = ref(0)
    const hasScrolledToBottom = ref(false)
    const minStayTime = 30 // 최소 30초 체류
    let stayInterval = null

    // YouTube 비디오 ID 추출
    const videoId = computed(() => {
      const url = props.lesson?.content || ''
      const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)
      return match ? match[1] : null
    })

    // YouTube IFrame API 로드
    function loadYouTubeAPI() {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve()
          return
        }

        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        const firstScript = document.getElementsByTagName('script')[0]
        firstScript.parentNode.insertBefore(tag, firstScript)

        window.onYouTubeIframeAPIReady = () => {
          resolve()
        }
      })
    }

    // YouTube 플레이어 초기화
    async function initYouTubePlayer() {
      if (!videoId.value) return

      await loadYouTubeAPI()
      
      await nextTick()
      
      const containerId = `youtube-player-${props.lesson.idx}`
      const container = document.getElementById(containerId)
      if (!container) return

      // 기존 플레이어 제거
      if (player.value) {
        player.value.destroy()
        player.value = null
      }

      player.value = new window.YT.Player(containerId, {
        videoId: videoId.value,
        width: '100%',
        height: '100%',
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1
        },
        events: {
          onReady: onPlayerReady,
          onStateChange: onPlayerStateChange
        }
      })
    }

    function onPlayerReady(event) {
      isVideoReady.value = true
      totalDuration.value = event.target.getDuration()
      
      // 이전 진행 상태 복원
      if (currentProgress.value > 0 && totalDuration.value > 0) {
        const savedTime = (currentProgress.value / 100) * totalDuration.value
        watchedTime.value = savedTime
      }
    }

    function onPlayerStateChange(event) {
      // 재생 중일 때만 추적
      if (event.data === window.YT.PlayerState.PLAYING) {
        startProgressTracking()
      } else {
        stopProgressTracking()
      }
    }

    function startProgressTracking() {
      if (progressInterval) return
      
      progressInterval = setInterval(() => {
        if (!player.value) return
        
        const currentTime = player.value.getCurrentTime()
        
        // 시청 시간 누적 (중복 방지 로직)
        updateWatchedTime(currentTime)
        
        // 진행률 계산
        if (totalDuration.value > 0) {
          currentProgress.value = Math.min(100, Math.round((watchedTime.value / totalDuration.value) * 100))
        }
        
        // 주기적 저장 (10초마다)
        if (Math.floor(currentTime) % 10 === 0) {
          saveProgress()
        }
      }, 1000)
    }

    function stopProgressTracking() {
      if (progressInterval) {
        clearInterval(progressInterval)
        progressInterval = null
        saveProgress()
      }
    }

    // 시청 시간 업데이트 (건너뛰기 방지)
    function updateWatchedTime(currentTime) {
      const segmentSize = 5 // 5초 단위로 체크
      const segment = Math.floor(currentTime / segmentSize)
      
      if (!watchedSegments.value.includes(segment)) {
        watchedSegments.value.push(segment)
        watchedTime.value = watchedSegments.value.length * segmentSize
      }
    }

    // 글 스크롤 & 체류 시간 추적
    function handleScroll() {
      if (!contentRef.value) return
      
      const { scrollTop, scrollHeight, clientHeight } = contentRef.value
      const scrollable = scrollHeight - clientHeight
      
      if (scrollable > 0) {
        const scrollPercent = Math.round((scrollTop / scrollable) * 100)
        
        if (scrollPercent >= 95) {
          hasScrolledToBottom.value = true
        }
        
        // 읽기 진행률 = 스크롤 + 체류시간 복합
        const scrollWeight = hasScrolledToBottom.value ? 50 : (scrollPercent / 2)
        const stayWeight = Math.min(50, (stayTime.value / minStayTime) * 50)
        readingProgress.value = Math.round(scrollWeight + stayWeight)
      }
    }

    function startStayTimer() {
      if (stayInterval) return
      
      stayInterval = setInterval(() => {
        stayTime.value++
        
        // 진행률 업데이트
        if (hasScrolledToBottom.value) {
          const stayWeight = Math.min(50, (stayTime.value / minStayTime) * 50)
          readingProgress.value = Math.round(50 + stayWeight)
        }
      }, 1000)
    }

    // 완료 가능 여부
    const canComplete = computed(() => {
      if (currentStatus.value === 'completed') return false
      
      if (props.lesson?.content_type === 'video') {
        return currentProgress.value >= 90
      } else {
        return canCompleteArticle.value
      }
    })

    const canCompleteArticle = computed(() => {
      return hasScrolledToBottom.value && stayTime.value >= minStayTime
    })

    // 마크다운 렌더링
    const renderedContent = computed(() => {
      let content = props.lesson?.content || ''
      content = content.replace(/\n/g, '<br>')
      content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      content = content.replace(/\*(.*?)\*/g, '<em>$1</em>')
      content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
      return content
    })

    // 이전/다음 레슨
    const currentIndex = computed(() => 
      props.allLessons?.findIndex(l => l.idx === props.lesson?.idx) ?? -1
    )

    const prevLesson = computed(() => 
      currentIndex.value > 0 ? props.allLessons[currentIndex.value - 1] : null
    )

    const nextLesson = computed(() => 
      currentIndex.value < (props.allLessons?.length || 0) - 1 
        ? props.allLessons[currentIndex.value + 1] 
        : null
    )

    // 진행 상태 로드
    async function loadProgress() {
      if (!props.lesson?.idx) return

      const mbId = getMbid()
      const res = await sql_query(`
        SELECT status, progress, position 
        FROM user_lesson_progress 
        WHERE mb_id = '${mbId}' AND lesson_idx = ${props.lesson.idx}
      `)

      if (res && res.length > 0) {
        currentProgress.value = res[0].progress || 0
        currentStatus.value = res[0].status || 'not_started'
        watchedTime.value = res[0].position || 0
      } else {
        currentProgress.value = 0
        currentStatus.value = 'not_started'
        watchedTime.value = 0
      }
      
      // 리셋
      watchedSegments.value = []
      readingProgress.value = 0
      stayTime.value = 0
      hasScrolledToBottom.value = false
    }

    // 진행 상태 저장
    async function saveProgress() {
      if (!props.lesson?.idx) return

      const mbId = getMbid()
      const progress = props.lesson.content_type === 'video' 
        ? currentProgress.value 
        : readingProgress.value
      
      const status = currentStatus.value === 'completed' 
        ? 'completed' 
        : progress > 0 ? 'in_progress' : 'not_started'

      await sql_query(`
        INSERT INTO user_lesson_progress (mb_id, lesson_idx, status, progress, position, updated_at)
        VALUES ('${mbId}', ${props.lesson.idx}, '${status}', ${progress}, ${Math.floor(watchedTime.value)}, NOW())
        ON DUPLICATE KEY UPDATE 
          status = '${status}',
          progress = ${progress},
          position = ${Math.floor(watchedTime.value)},
          updated_at = NOW()
      `)

      if (props.onProgressUpdate) {
        props.onProgressUpdate(props.lesson.idx, status, progress)
      }
    }

    // 완료 처리
    async function markComplete() {
      if (!canComplete.value) return
      
      currentStatus.value = 'completed'
      currentProgress.value = 100

      const mbId = getMbid()
      await sql_query(`
        INSERT INTO user_lesson_progress (mb_id, lesson_idx, status, progress, position, completed_at, updated_at)
        VALUES ('${mbId}', ${props.lesson.idx}, 'completed', 100, ${Math.floor(watchedTime.value)}, NOW(), NOW())
        ON DUPLICATE KEY UPDATE 
          status = 'completed',
          progress = 100,
          completed_at = NOW(),
          updated_at = NOW()
      `)

      if (props.onProgressUpdate) {
        props.onProgressUpdate(props.lesson.idx, 'completed', 100)
      }
    }

    // 레슨 이동
    function goToLesson(lesson) {
      if (!lesson) return
      
      cleanup()
      saveProgress()
      
      Object.assign(props.lesson, lesson)
      loadProgress()
      
      nextTick(() => {
        if (lesson.content_type === 'video') {
          initYouTubePlayer()
        }
      })
      
      if (contentRef.value) {
        contentRef.value.scrollTop = 0
      }
    }

    function handleClose() {
      cleanup()
      saveProgress()
      props.windowOnOff(false)
    }

    function cleanup() {
      stopProgressTracking()
      if (stayInterval) {
        clearInterval(stayInterval)
        stayInterval = null
      }
      if (player.value) {
        player.value.destroy()
        player.value = null
      }
      isVideoReady.value = false
    }

    function formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${String(secs).padStart(2, '0')}`
    }

    function formatDuration(seconds) {
      const min = Math.floor(seconds / 60)
      if (min < 60) return `${min}분`
      const hour = Math.floor(min / 60)
      const remMin = min % 60
      return `${hour}시간 ${remMin}분`
    }

    // 레슨 변경 감지
    watch(() => props.lesson?.idx, () => {
      cleanup()
      loadProgress()
      
      nextTick(() => {
        if (props.lesson?.content_type === 'video') {
          initYouTubePlayer()
        } else {
          startStayTimer()
        }
      })
    })

    onMounted(() => {
      loadProgress()
      
      if (props.lesson?.content_type === 'video') {
        initYouTubePlayer()
      } else {
        startStayTimer()
        if (contentRef.value) {
          contentRef.value.addEventListener('scroll', handleScroll)
        }
      }
    })

    onUnmounted(() => {
      cleanup()
      saveProgress()
      
      if (contentRef.value) {
        contentRef.value.removeEventListener('scroll', handleScroll)
      }
    })

    return {
      contentRef,
      currentProgress,
      currentStatus,
      isVideoReady,
      watchedTime,
      totalDuration,
      readingProgress,
      stayTime,
      minStayTime,
      canComplete,
      canCompleteArticle,
      renderedContent,
      prevLesson,
      nextLesson,
      markComplete,
      goToLesson,
      handleClose,
      formatTime,
      formatDuration
    }
  }
}
</script>


<style scoped>
#dialog-curriculumView {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: var(--color-background);
  z-index: var(--z-index-top);
}

.curriculum-view {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.curriculum-view > .header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  color: var(--color-text-primary);
  padding: 16px;
  box-sizing: border-box;
}

.curriculum-view > .header > p {
  aspect-ratio: 1/1;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.curriculum-view > .header > h2 {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
  flex: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content {
  width: 100%;
  max-width: 900px;
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

/* 영상 */
.video-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.video-wrapper {
  position: relative;
  width: 100%;
  background: var(--color-text-black);
  border-radius: var(--radius-lg);
  overflow: hidden;
  aspect-ratio:16/9;
}

.video-wrapper .youtube-player,
.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  gap: 12px;
  background: var(--color-background-light);
}

.video-loading i {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 자동 진행률 */
.auto-progress {
  padding: 16px;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.progress-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
}

.progress-bar {
  height: 8px;
  background: var(--color-background);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-brand);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.requirement {
  color: var(--color-special-warning);
}

.complete-ready {
  color: #22c55e;
  font-weight: var(--font-weight-semibold);
}

/* 글 콘텐츠 */
.article-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.article-content {
  padding: 24px;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
  color: var(--color-text-primary);
  font-size: var(--font-size-md);
  line-height: 1.8;
  word-break: keep-all;
  min-height: 300px;
}

.article-content a {
  color: var(--color-special-accent);
  text-decoration: underline;
}

/* 레슨 정보 */
.lesson-info-section {
  margin-top: 24px;
  padding: 20px;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.info-row i {
  font-size: var(--font-size-lg);
}

.complete-section {
  display: flex;
  justify-content: center;
}

.complete-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 2px solid var(--color-text-third);
  background: transparent;
  color: var(--color-text-secondary);
  border-radius: 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: not-allowed;
  transition: all 0.2s ease;
  opacity: 0.5;
}

.complete-btn.can-complete {
  border-color: var(--color-brand);
  color: var(--color-brand);
  cursor: pointer;
  opacity: 1;
}

.complete-btn.can-complete:hover {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.complete-btn.completed {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  cursor: default;
  opacity: 1;
}

.complete-btn i {
  font-size: var(--font-size-lg);
}

/* 네비게이션 */
.navigation-section {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.nav-btn {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--color-background-light);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  color: var(--color-text-primary);
}

.nav-btn:hover:not(:disabled) {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.nav-btn.next {
  text-align: right;
  justify-content: flex-end;
}

.nav-btn i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.nav-btn:hover:not(:disabled) i {
  color: var(--color-text-white);
}

.nav-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.nav-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.nav-btn:hover:not(:disabled) .nav-label {
  color: rgba(255,255,255,0.8);
}

.nav-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-btn:hover:not(:disabled) .nav-title {
  color: var(--color-text-white);
}

@media (max-width: 600px) {
  .navigation-section {
    flex-direction: column;
  }
  
  .nav-btn.next {
    text-align: left;
    justify-content: flex-start;
  }
}
</style>